import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  TextField,
  CircularProgress,
  Collapse,
  Alert,
  Link,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import KeyIcon from "@mui/icons-material/Key";
import BottomNav from "./BottomNav";

const WHENISEEUS_URL = "https://wheniseeus.com";
// En dev (localhost) pegamos contra el backend local de wheniseeus; en
// producción, contra wheniseeus.com.
const WHENISEEUS_API_BASE =
  typeof window !== "undefined" && window.location.hostname === "localhost"
    ? "http://localhost:8000"
    : "https://wheniseeus.com";
// Endpoint dedicado para Skach: devuelve JSON { code, language, raw }.
const WHENISEEUS_API = `${WHENISEEUS_API_BASE}/api/skach/code`;
const TOKEN_KEY = "wheniseeus_token";

// Lenguajes que un preview en el navegador puede ejecutar en vivo.
// (Los de backend —Python, etc.— necesitarían un runtime aparte.)
const LANGUAGES = [
  { value: "react", label: "React / JSX" },
  { value: "tsx", label: "TypeScript / TSX" },
  { value: "html", label: "HTML / CSS" },
  { value: "javascript", label: "JavaScript" },
  { value: "typescript", label: "TypeScript" },
  { value: "vue", label: "Vue 3" },
];

const STARTERS = {
  html: `<h1>Hola desde Skach</h1>
<p>Pegá acá el código que te da wheniseeus y miralo en vivo.</p>
<button onclick="this.textContent='¡Funciona!'">Tocame</button>`,
  react: `function App() {
  const [n, setN] = React.useState(0);
  return (
    <div style={{ fontFamily: 'system-ui' }}>
      <h1>Hola desde Skach</h1>
      <button onClick={() => setN(n + 1)}>Clicks: {n}</button>
    </div>
  );
}`,
  tsx: `function App(): JSX.Element {
  const [n, setN] = React.useState<number>(0);
  return (
    <div style={{ fontFamily: 'system-ui' }}>
      <h1>Hola desde Skach (TSX)</h1>
      <button onClick={() => setN(n + 1)}>Clicks: {n}</button>
    </div>
  );
}`,
  javascript: `document.body.innerHTML = '<h1>Hola desde Skach</h1>';
const p = document.createElement('p');
p.textContent = 'Resultado de tu JavaScript en vivo.';
document.body.appendChild(p);
console.log('Hola consola');`,
  typescript: `const saludo = (nombre: string): string => 'Hola, ' + nombre;
document.body.innerHTML = '<h1>' + saludo('Skach') + '</h1>';`,
  vue: `const { createApp, ref } = Vue;
createApp({
  setup() {
    const n = ref(0);
    return { n };
  },
  template: \`
    <div style="font-family:system-ui">
      <h1>Hola desde Skach (Vue)</h1>
      <button @click="n++">Clicks: {{ n }}</button>
    </div>\`,
}).mount('#app');`,
};

const REACT_CDN = `<script crossorigin src="https://unpkg.com/react@18.3.1/umd/react.development.js"></script>
<script crossorigin src="https://unpkg.com/react-dom@18.3.1/umd/react-dom.development.js"></script>
<script src="https://unpkg.com/@babel/standalone@7.26.4/babel.min.js"></script>`;

// Tailwind por CDN: el modelo suele responder con clases de Tailwind, así que
// las cargamos para que el preview se vea con estilo.
const TAILWIND_CDN = `<script src="https://cdn.tailwindcss.com"></script>`;

// React/ReactDOM vienen como globales (UMD), así que exponemos los hooks más
// usados para que el código que los usa sin importar funcione.
const REACT_PREAMBLE =
  "const { useState, useEffect, useRef, useMemo, useCallback, useContext, useReducer, useLayoutEffect, Fragment } = React;";

// El código del modelo viene con sintaxis de bundler (import/export) que el
// navegador no resuelve. Los quitamos: React y los hooks ya son globales.
const prepareReactCode = (code) =>
  (code || "")
    .replace(/^\s*import\s[^;]*;?\s*$/gm, "")
    .replace(/export\s+default\s+/g, "")
    .replace(/^\s*export\s+(?=(const|let|var|function|class)\b)/gm, "");

const RENDER_APP = `;(function () {
  try {
    var el = document.getElementById('root');
    if (typeof App !== 'undefined') {
      ReactDOM.createRoot(el).render(React.createElement(App));
    } else {
      document.body.innerHTML =
        '<pre style="color:#888">No encontré un componente llamado App para renderizar.</pre>';
    }
  } catch (e) {
    document.body.innerHTML =
      '<pre style="color:#c0392b;white-space:pre-wrap">' + String(e) + '</pre>';
  }
})();`;

const page = (head, body) =>
  `<!doctype html><html><head><meta charset="utf-8">
<style>body{font-family:system-ui;margin:16px}</style>
${head}</head><body>${body}</body></html>`;

// Arma el documento que se renderiza en el iframe de preview según el lenguaje.
const buildDoc = (code, mode) => {
  switch (mode) {
    case "html":
      return /<html|<!doctype/i.test(code) ? code : page(TAILWIND_CDN, code);

    case "react":
      return page(
        REACT_CDN + TAILWIND_CDN,
        `<div id="root"></div>
<script type="text/babel" data-presets="react">
${REACT_PREAMBLE}
${prepareReactCode(code)}
${RENDER_APP}
</script>`
      );

    case "tsx":
      return page(
        REACT_CDN + TAILWIND_CDN,
        `<div id="root"></div>
<script type="text/babel" data-presets="typescript,react">
${REACT_PREAMBLE}
${prepareReactCode(code)}
${RENDER_APP}
</script>`
      );

    case "typescript":
      return page(
        `<script src="https://unpkg.com/@babel/standalone@7.26.4/babel.min.js"></script>`,
        `<script type="text/babel" data-presets="typescript">
${code}
</script>`
      );

    case "javascript":
      return page(
        "",
        `<script>
try { ${code} } catch (e) {
  document.body.innerHTML =
    '<pre style="color:#c0392b;white-space:pre-wrap">' + String(e) + '</pre>';
}
</script>`
      );

    case "vue":
      return page(
        `<script src="https://unpkg.com/vue@3.5.13/dist/vue.global.prod.js"></script>`,
        `<div id="app"></div>
<script>
try { ${code} } catch (e) {
  document.body.innerHTML =
    '<pre style="color:#c0392b;white-space:pre-wrap">' + String(e) + '</pre>';
}
</script>`
      );

    default:
      return page("", code);
  }
};

// Mapea el lenguaje del bloque ``` de la respuesta a uno de nuestros modos.
const FENCE_TO_MODE = {
  jsx: "react",
  react: "react",
  tsx: "tsx",
  ts: "typescript",
  typescript: "typescript",
  js: "javascript",
  javascript: "javascript",
  html: "html",
  css: "html",
  vue: "vue",
};

// Si el texto trae un bloque ```lang ... ```, devolvemos el código y el modo.
const extractCode = (text) => {
  const m = /```(\w+)?\s*\n([\s\S]*?)```/.exec(text || "");
  if (!m) return { lang: null, body: (text || "").trim() };
  const lang = m[1] ? FENCE_TO_MODE[m[1].toLowerCase()] : null;
  return { lang: lang || null, body: m[2].trim() };
};

const Workspace = () => {
  const [mode, setMode] = useState("react");
  const [code, setCode] = useState(STARTERS.react);
  const [doc, setDoc] = useState("");

  // Asistente wheniseeus
  const [prompt, setPrompt] = useState("");
  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Token guardado en el navegador (no se envía a ningún lado salvo a wheniseeus).
  useEffect(() => {
    try {
      const saved = localStorage.getItem(TOKEN_KEY);
      if (saved) setToken(saved);
    } catch (e) {
      /* localStorage no disponible */
    }
  }, []);

  const saveToken = (value) => {
    setToken(value);
    try {
      localStorage.setItem(TOKEN_KEY, value);
    } catch (e) {
      /* no-op */
    }
  };

  // Live preview con un pequeño debounce para no re-renderizar en cada tecla.
  useEffect(() => {
    const id = setTimeout(() => setDoc(buildDoc(code, mode)), 400);
    return () => clearTimeout(id);
  }, [code, mode]);

  // Le pide código a wheniseeus y lo carga en el editor.
  const askWheniseeus = async () => {
    if (!prompt.trim() || loading) return;
    if (!token.trim()) {
      setShowToken(true);
      setError("Ingresá tu token de wheniseeus para poder generar código.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(WHENISEEUS_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.trim()}`,
        },
        body: JSON.stringify({ prompt, language: mode }),
      });
      if (!res.ok) {
        let detail = "";
        try {
          detail = (await res.json()).detail || "";
        } catch (_) {
          detail = (await res.text()).slice(0, 200);
        }
        throw new Error(`wheniseeus respondió ${res.status}: ${detail}`);
      }
      const data = await res.json();
      // El endpoint ya devuelve el código extraído del bloque. Como respaldo,
      // si viniera con fences, los limpiamos y detectamos el lenguaje.
      const { lang, body } = extractCode(data.code || "");
      const detected = FENCE_TO_MODE[(data.language || "").toLowerCase()] || lang;
      if (detected) setMode(detected);
      setCode(body || data.code || data.raw || "");
    } catch (e) {
      setError(
        e.message ||
          "No se pudo contactar a wheniseeus. Revisá el token y volvé a intentar."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleMode = (e) => {
    const next = e.target.value;
    if (!next || next === mode) return;
    // Si el editor sigue con un starter (o está vacío), cargamos el del lenguaje nuevo.
    const isStarter =
      code.trim() === "" ||
      Object.values(STARTERS).some((s) => s === code);
    setMode(next);
    if (isStarter) setCode(STARTERS[next] || "");
  };

  const openWheniseeus = () =>
    window.open(WHENISEEUS_URL, "_blank", "noopener,noreferrer");

  const refresh = () => setDoc(buildDoc(code, mode));

  const headerActions = useMemo(
    () => (
      <Button
        variant="contained"
        startIcon={<AutoAwesomeIcon />}
        endIcon={<OpenInNewIcon fontSize="small" />}
        onClick={openWheniseeus}
      >
        Abrir wheniseeus
      </Button>
    ),
    []
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        pb: { xs: 9, md: 0 }, // espacio para la BottomNav en mobile
      }}
    >
      {/* Header de la página (no es AppBar, así se ve también en desktop) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          px: 2,
          py: 1.5,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AutoAwesomeIcon color="primary" />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              Workspace IA
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Trabajá en Skach con wheniseeus y mirá el resultado en vivo
            </Typography>
          </Box>
        </Box>
        {headerActions}
      </Box>

      {/* Barra del asistente wheniseeus */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <TextField
            fullWidth
            size="small"
            multiline
            maxRows={3}
            placeholder="Pedile a wheniseeus… ej: 'una landing con un formulario de contacto en React'"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) askWheniseeus();
            }}
            sx={{ flex: 1, minWidth: 240 }}
          />
          <Button
            variant="contained"
            onClick={askWheniseeus}
            disabled={loading || !prompt.trim()}
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SendIcon />
              )
            }
            sx={{ whiteSpace: "nowrap" }}
          >
            {loading ? "Generando…" : "Generar"}
          </Button>
          <Tooltip title="Token de wheniseeus">
            <IconButton onClick={() => setShowToken((v) => !v)}>
              <KeyIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Collapse in={showToken}>
          <Box sx={{ mt: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              type="password"
              label="API key o token de wheniseeus"
              value={token}
              onChange={(e) => saveToken(e.target.value)}
              helperText={
                <>
                  Pegá tu API key de wheniseeus (empieza con <code>wis_</code>) o
                  un ID token. Se guarda solo en este navegador y se envía
                  únicamente a wheniseeus. Generala en{" "}
                  <Link href={WHENISEEUS_URL} target="_blank" rel="noopener">
                    wheniseeus.com
                  </Link>
                  .
                </>
              }
            />
          </Box>
        </Collapse>

        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
      </Box>

      {/* Editor + preview */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          minHeight: 0,
        }}
      >
        {/* Editor */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            borderRight: { md: (theme) => `1px solid ${theme.palette.divider}` },
            borderBottom: {
              xs: (theme) => `1px solid ${theme.palette.divider}`,
              md: "none",
            },
            minHeight: { xs: 300, md: "auto" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1,
            }}
          >
            <Select size="small" value={mode} onChange={handleMode}>
              {LANGUAGES.map((l) => (
                <MenuItem key={l.value} value={l.value}>
                  {l.label}
                </MenuItem>
              ))}
            </Select>
            <Tooltip title="Actualizar preview">
              <IconButton size="small" onClick={refresh}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            component="textarea"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            spellCheck={false}
            sx={{
              flex: 1,
              width: "100%",
              resize: "none",
              border: "none",
              outline: "none",
              p: 2,
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 13,
              lineHeight: 1.6,
              bgcolor: "background.paper",
              color: "text.primary",
              boxSizing: "border-box",
            }}
          />
        </Box>

        {/* Live preview */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: { xs: 320, md: "auto" },
          }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Vista previa
            </Typography>
          </Box>
          <Box
            component="iframe"
            title="Vista previa"
            srcDoc={doc}
            sandbox="allow-scripts allow-modals allow-forms"
            sx={{
              flex: 1,
              width: "100%",
              border: "none",
              bgcolor: "#fff",
            }}
          />
        </Box>
      </Box>

      <BottomNav />
    </Box>
  );
};

export default Workspace;
